import {
  Box,
  Divider,
  Grid,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import { Info, Receipt } from '@material-ui/icons'
import jsonExport from 'jsonexport/dist'
import React, { cloneElement, useCallback, useContext, useEffect } from 'react'
import {
  BooleanInput,
  Datagrid,
  DateField,
  downloadCSV,
  ExportButton,
  Filter,
  FilterButton,
  FilterContext,
  FunctionField,
  ListActionsProps,
  ListProps,
  Pagination,
  Record,
  sanitizeListRestProps,
  ShowButton,
  TextField,
  TextInput,
  TopToolbar,
  useListContext,
  useTranslate,
  useQueryWithStore,
  DateTimeInput,
  useMutation,
} from 'react-admin'
import { useHistory } from 'react-router'
import { Authority } from '../../core/auth/Authority'
import { getContrastColor } from '../../core/common/visual.utils'
import { DeviceBaseWithTypeInfoDto } from '../../core/dto/device/common/device-base/device-base-with-type-info.dto'
import { ExtendedLog } from '../../core/dto/extended-log'
import { ResourceName } from '../../core/ResourceName'
import List from '../common/customized-ra-components/List'
import FilteredReferenceInput from '../common/FilteredReferenceInput'
import QuickFilter from '../common/QuickFilter'
import { OpenCameraPreviewDialogButton } from '../devices/camera/cameras/CameraPreviewDialog'
import { useHasAuthority } from '../hooks/useHasAuthority'
import { VCenterGrid, VHCenterGrid } from './LogsComponents'
import { dateTimeInputDefaultParse } from '../../core/common/date-time'
import { UserSettingsDto } from '../../core/dto/user-settings.dto'
import { TextFieldWithMaxChars } from '../common/TextFieldWithMaxChars'
import { getDeviceIcon } from '../../core/devices/devices.components'

interface LogExpandProps {
  readonly noBooking?: boolean
  readonly noCamera?: boolean
  readonly record?: ExtendedLog
  readonly ticketCodeMaxChars?: number
}

interface LogListProps extends ListProps {
  readonly compactMode?: boolean
  readonly noBooking?: boolean
  readonly noCamera?: boolean
  readonly usePesistentFilters?: boolean
}

const BookingButton = ({ ...props }) => {
  const { style } = props
  const theme = useTheme()
  const xSmallScreen = useMediaQuery(theme.breakpoints.down('xs'))
  const history = useHistory()
  return (
    <FunctionField
      source="bookingId"
      render={(record: Record | undefined) =>
        record?.bookingId && (
          <>
            <ShowButton
              color="inherit"
              basePath="/bookings"
              label={record.bookingId.toString()}
              record={{ id: record.bookingId }}
              icon={<Receipt />}
              style={style}
            />
            {xSmallScreen && (
              <Typography
                color="inherit"
                onClick={() =>
                  history.push(`/bookings/${record.bookingId}/show`)
                }
              >
                {record.bookingId}
              </Typography>
            )}
          </>
        )
      }
    />
  )
}

const LogExpand = ({
  noBooking,
  noCamera,
  record,
  ticketCodeMaxChars = 10,
}: LogExpandProps) => {
  const translate = useTranslate()
  return (
    <Box padding={1}>
      <Grid container spacing={1}>
        {record?.ticketCode?.length &&
          record?.ticketCode?.length > ticketCodeMaxChars && (
            <>
              <VCenterGrid item xs={3}>
                {translate('resources.logs.fields.ticketCode')}:
              </VCenterGrid>
              <VCenterGrid item xs={9}>
                <TextFieldWithMaxChars source="ticketCode" maxChars={36} />
              </VCenterGrid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
            </>
          )}
        <VCenterGrid item xs={3}>
          {translate('resources.logs.fields.description')}:
        </VCenterGrid>
        <Grid item xs={9}>
          <TextFieldWithMaxChars maxChars={28} source="text" />
        </Grid>
        <VCenterGrid item xs={1}>
          <FunctionField
            key="note"
            render={(logRecord) =>
              logRecord?.note ? (
                <Tooltip title={logRecord?.note}>
                  <Info />
                </Tooltip>
              ) : (
                <div />
              )
            }
          />
        </VCenterGrid>
        {!noBooking && record?.bookingId && (
          <>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <VCenterGrid item xs={4}>
              {translate('resources.logs.fields.bookingId')}:
            </VCenterGrid>
            <VHCenterGrid item xs={8}>
              <BookingButton />
            </VHCenterGrid>
          </>
        )}
        {!noCamera &&
          record?.cameraId !== null &&
          record?.cameraId !== undefined && (
            <>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <VCenterGrid item xs={4}>
                {translate('resources.logs.fields.cameraId')}:
              </VCenterGrid>
              <VHCenterGrid item xs={8}>
                <OpenCameraPreviewDialogButton
                  startDate={record.date}
                  cameraData={{
                    id: record?.cameraId,
                  }}
                  logId={record.id}
                  label=""
                  props={{ variant: undefined }}
                />
              </VHCenterGrid>
            </>
          )}
      </Grid>
    </Box>
  )
}

const LogFilters = ({ usePesistentFilters, ...props }) => {
  const hasAuthority = useHasAuthority()
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const { filterValues } = props
  const [mutate] = useMutation()

  const saveUserSettingsState = useCallback(
    async (settings: UserSettingsDto) => {
      await mutate({
        type: 'patchSettings',
        resource: ResourceName.CURRENT_USER,
        payload: settings,
      })
    },
    [mutate],
  )

  useEffect(() => {
    const settings = {
      viewState_Logs_Filters_SimpleView: filterValues?.simpleView || false,
      viewState_Logs_Filters_LastDay: filterValues?.lastDay || false,
      viewState_Logs_Filters_LastWeek: filterValues?.lastWeek || false,
      viewState_Logs_Filters_LastMonth: filterValues?.lastMonth || false,
      viewState_Logs_Filters_BookingId: filterValues?.bookingId || null,
      viewState_Logs_Filters_DeviceId: filterValues?.deviceId || null,
      viewState_Logs_Filters_DeviceCategoryId:
        filterValues?.categoryIdWithDescendants || null,
      viewState_Logs_Filters_EventId: filterValues?.eventId || null,
      viewState_Logs_Filters_ObjectId: filterValues?.objectId || null,
      viewState_Logs_Filters_SchemeId:
        filterValues?.schemeIdWithDescendants || null,
      viewState_Logs_Filters_DateFrom: filterValues?.dateFrom || null,
      viewState_Logs_Filters_DateTo: filterValues?.dateTo || null,
    } as UserSettingsDto

    if (usePesistentFilters) saveUserSettingsState(settings)
  }, [filterValues, saveUserSettingsState, usePesistentFilters])

  const requiredDevicePermissions = [
    Authority.VIEW_BARRIERS,
    Authority.VIEW_DEPOSITORS,
    Authority.VIEW_DETECTORS,
    Authority.VIEW_DOORS,
    Authority.VIEW_ENGINES,
    Authority.VIEW_OFFLINE_SERVERS,
    Authority.VIEW_PALMS,
    Authority.VIEW_PASSAGES,
    Authority.VIEW_TERMINALS,
  ]

  return (
    <Filter {...props}>
      <TextInput key="ticketCode" source="ticketCode" alwaysOn />
      <BooleanInput source="simpleView" alwaysOn />
      {requiredDevicePermissions.some(hasAuthority) && (
        <FilteredReferenceInput
          source="deviceId"
          reference={ResourceName.DEVICES_BASE}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={smallScreen ? 5 : 15}
          filterSource="search"
          selectWithPaginationInputProps={{
            optionText: 'name',
            showFilter: true,
            rowIcon: (record: DeviceBaseWithTypeInfoDto) =>
              getDeviceIcon(record?.deviceType),
          }}
        />
      )}
      <TextInput
        source="deviceName"
        key="deviceName"
        label="resources.logs.filters.deviceName"
      />
      {hasAuthority(Authority.VIEW_DEVICE_CATEGORIES) && (
        <FilteredReferenceInput
          label="resources.logs.filters.deviceCategoryId"
          source="categoryIdWithDescendants"
          reference={ResourceName.DEVICE_CATEGORIES}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={smallScreen ? 5 : 15}
          filterSource="search"
          selectWithPaginationInputProps={{
            optionText: 'hierarchyString',
            showFilter: true,
          }}
        />
      )}
      {hasAuthority(Authority.VIEW_SCHEMES) && (
        <FilteredReferenceInput
          label="resources.logs.filters.schemeId"
          source="schemeIdWithDescendants"
          reference={ResourceName.SCHEMES}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={smallScreen ? 5 : 15}
          filterSource="translatedLogSchemeDataTextSearch"
          selectWithPaginationInputProps={{
            optionText: 'translatedLogSchemeDataHierarchyString',
            showFilter: true,
          }}
        />
      )}
      <TextInput source="bookingId" key="bookingId" />
      <DateTimeInput
        source="dateFrom"
        key="dateFrom"
        parse={dateTimeInputDefaultParse}
      />
      <DateTimeInput
        source="dateTo"
        key="dateTo"
        parse={dateTimeInputDefaultParse}
      />
      <QuickFilter source="lastDay" key="lastDay" defaultValue />
      <QuickFilter source="lastWeek" key="lastWeek" defaultValue />
      <QuickFilter source="lastMonth" key="lastMonth" defaultValue />
      {hasAuthority(Authority.VIEW_VARIANTS) && (
        <FilteredReferenceInput
          source="variantId"
          reference={ResourceName.VARIANTS}
          sort={{ field: 'id', order: 'ASC' }}
          perPage={smallScreen ? 5 : 15}
          filterSource="name"
          selectWithPaginationInputProps={{
            optionText: 'name',
            showFilter: true,
          }}
        />
      )}
      {hasAuthority(Authority.VIEW_OBJECTS) && (
        <FilteredReferenceInput
          source="objectId"
          reference={ResourceName.OBJECTS}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={smallScreen ? 5 : 15}
          filterSource="search"
          selectWithPaginationInputProps={{
            optionText: 'name',
            showFilter: true,
          }}
        />
      )}
      {hasAuthority(Authority.VIEW_EVENTS) && (
        <FilteredReferenceInput
          label="resources.logs.fields.event"
          source="eventId"
          reference={ResourceName.EVENTS}
          sort={{ field: 'dateStart', order: 'ASC' }}
          perPage={smallScreen ? 5 : 15}
          filterSource="nameWithDateSearch"
          selectWithPaginationInputProps={{
            optionText: 'nameWithDate',
            showFilter: true,
          }}
        />
      )}
    </Filter>
  )
}

const LogActions = (props: ListActionsProps) => {
  const { className, exporter, filters: filtersProp, ...rest } = props
  const { currentSort, displayedFilters, filterValues, showFilter, total } =
    useListContext(props)
  const resource = ResourceName.LOGS
  const filters = useContext(FilterContext) || filtersProp
  return (
    <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
      {filtersProp
        ? cloneElement(filtersProp, {
            resource,
            showFilter,
            displayedFilters,
            filterValues,
            context: 'button',
          })
        : filters && <FilterButton />}
      {exporter !== false && (
        <ExportButton
          disabled={total === 0 || total >= 150000}
          resource={resource}
          sort={currentSort}
          filterValues={filterValues}
          maxResults={150000}
        />
      )}
    </TopToolbar>
  )
}

const LogListExport = (extendedLogs: ExtendedLog[]) => {
  const extendedLogsForExport = extendedLogs.map((log) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { cameraId, color, ...postForExport } = log
    return postForExport
  })
  jsonExport(extendedLogsForExport, (err, csv) => {
    downloadCSV(csv, 'logs')
  })
}

export const LogList = ({
  compactMode = false,
  noBooking = false,
  noCamera = false,
  usePesistentFilters = false,
  ...props
}: LogListProps) => {
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const xSmallScreen = useMediaQuery(theme.breakpoints.down('xs'))
  const hasAuthority = useHasAuthority()

  const { data: settings, loaded: settingsLoaded } = useQueryWithStore({
    type: 'getSettings',
    resource: ResourceName.CURRENT_USER,
    payload: {},
  })

  const filterDefaultValues = {
    simpleView: settings?.viewState_Logs_Filters_SimpleView || undefined,
    lastDay: settings?.viewState_Logs_Filters_LastDay || undefined,
    lastWeek: settings?.viewState_Logs_Filters_LastWeek || undefined,
    lastMonth: settings?.viewState_Logs_Filters_LastMonth || undefined,
    bookingId: settings?.viewState_Logs_Filters_BookingId || undefined,
    deviceId: settings?.viewState_Logs_Filters_DeviceId || undefined,
    categoryIdWithDescendants:
      settings?.viewState_Logs_Filters_DeviceCategoryId || undefined,
    eventId: settings?.viewState_Logs_Filters_EventId || undefined,
    objectId: settings?.viewState_Logs_Filters_ObjectId || undefined,
    schemeIdWithDescendants:
      settings?.viewState_Logs_Filters_SchemeId || undefined,
    dateFrom: settings?.viewState_Logs_Filters_DateFrom || undefined,
    dateTo: settings?.viewState_Logs_Filters_DateTo || undefined,
  }

  const ticketCodeMaxChars = 8

  return (
    <List
      exporter={LogListExport}
      perPage={20}
      pagination={<Pagination rowsPerPageOptions={[10, 20, 50, 100, 200]} />}
      filters={
        settingsLoaded ? (
          <LogFilters usePesistentFilters={usePesistentFilters} />
        ) : undefined
      }
      actions={<LogActions />}
      sort={{ field: 'date', order: 'DESC' }}
      filterDefaultValues={
        settingsLoaded && usePesistentFilters ? filterDefaultValues : undefined
      }
      {...props}
      bulkActionButtons={false}
    >
      <Datagrid
        expand={
          smallScreen || compactMode ? (
            <LogExpand
              noBooking={noBooking}
              noCamera={noCamera}
              ticketCodeMaxChars={ticketCodeMaxChars}
            />
          ) : undefined
        }
        rowStyle={(record: Record) => ({
          background: record && record.color,
          color:
            record && record.color !== 'transparent'
              ? getContrastColor(record.color)
              : 'inherit',
        })}
        padding={xSmallScreen ? 'checkbox' : 'default'}
      >
        <DateField
          source="date"
          options={
            xSmallScreen
              ? {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  year: '2-digit',
                  month: '2-digit',
                  day: '2-digit',
                }
              : {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                }
          }
        />
        <TextField source="deviceName" />
        {(!compactMode && !smallScreen && (
          <TextField source="ticketCode" />
        )) || (
          <TextFieldWithMaxChars
            source="ticketCode"
            maxChars={ticketCodeMaxChars}
          />
        )}
        {!compactMode && !smallScreen && (
          <TextField
            source="text"
            label="resources.logs.fields.description"
            sortable={false}
          />
        )}
        {!compactMode &&
          !noBooking &&
          !smallScreen &&
          (hasAuthority(Authority.VIEW_BOOKINGS) ? (
            <BookingButton source="bookingId" />
          ) : (
            <TextField source="bookingId" />
          ))}
        {!compactMode && !smallScreen && <TextField source="scheme" />}
        {!compactMode && !smallScreen && (
          <FunctionField
            key="note"
            render={(record) =>
              record?.note ? (
                <Tooltip title={record?.note}>
                  <Info />
                </Tooltip>
              ) : (
                <div />
              )
            }
          />
        )}
        {!compactMode &&
          !noCamera &&
          !smallScreen &&
          (hasAuthority(Authority.CAMERAS_PREVIEW_BUTTON) ||
          hasAuthority(Authority.ALL_CAMERAS_PREVIEW_SELECT) ? (
            <FunctionField<ExtendedLog>
              source="cameraId"
              sortable={false}
              render={(record?: ExtendedLog) =>
                record?.cameraId !== null &&
                record?.cameraId !== undefined && (
                  <Box
                    display="inline-flex"
                    alignItems="center"
                    style={{ flexWrap: 'nowrap' }}
                  >
                    {hasAuthority(Authority.CAMERAS_PREVIEW_BUTTON) && (
                      <OpenCameraPreviewDialogButton
                        startDate={record.date}
                        cameraData={{ id: record?.cameraId }}
                        logId={record.id}
                        label=""
                        props={{ variant: undefined }}
                        showAllCamerasSelect
                      />
                    )}
                  </Box>
                )
              }
            />
          ) : (
            <TextField source="cameraId" sortable={false} />
          ))}
      </Datagrid>
    </List>
  )
}
